import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { NavStep } from '../../../@types';
import { IconType, KioskType } from '../../../@types';
import { AppContext } from '../../../context/app';
import { useKioskManager } from '../../../hooks/useKioskManager';
import { useNavigationManager } from '../../../hooks/useNavigationManager';
import { useNetworkManager } from '../../../hooks/useNetworkManager';
import ButtonStyled from '../button/Button';
import Icon from '../icon/Icon';
import { LegTwoVisualStep } from '../LegTwoVisualStep/LegTwoVisualStep';
import styles from './LegTwoVisualSteps.module.scss';

interface LegTwoVisualStepsProps {
  onExit?: () => void;
}
export const LegTwoVisualSteps: React.FC<LegTwoVisualStepsProps> = ({
  onExit,
}) => {
  const { legTwoFocusedStep, setLegTwoFocusedStep, showPrintDirectionView } =
    useContext(AppContext);
  const [ctsStepRange, setCtsStepRange] = useState<number>(0);
  const { legTwoRoute } = useNavigationManager();
  const { networkData } = useNetworkManager();
  const { kiosk } = useKioskManager();
  const { t } = useTranslation('common');
  const ctsStepWindow = showPrintDirectionView ? 40 : 4;

  const isCTS = useMemo(
    () => kiosk?.model === KioskType.CTS_DIGITAL_SIGNAGE,
    [kiosk?.model],
  );

  const selectedSite = useMemo(
    () =>
      networkData?.sites.find(
        (site) => site.id === legTwoRoute?.navSteps[0].polyLine[0].s,
      ),
    [legTwoRoute?.navSteps, networkData?.sites],
  );

  useEffect(() => {
    setLegTwoFocusedStep(0);
  }, [setLegTwoFocusedStep]);

  const goToNextPage = () => {
    if (
      legTwoRoute &&
      ctsStepRange + ctsStepWindow > legTwoRoute?.navSteps.length - 1
    ) {
      onExit && onExit();
      return;
    }
    setCtsStepRange(ctsStepRange + ctsStepWindow);
  };
  const currentPage = useMemo(
    () => ctsStepRange / ctsStepWindow + 1,
    [ctsStepRange, ctsStepWindow],
  );

  const totalPages = useMemo(() => {
    if (legTwoRoute) {
      return Math.ceil(legTwoRoute.navSteps.length / ctsStepWindow);
    }
  }, [ctsStepWindow, legTwoRoute]);

  const isLastPage = useMemo(
    () =>
      legTwoRoute &&
      ctsStepRange + ctsStepWindow > legTwoRoute?.navSteps.length - 1,
    [ctsStepRange, ctsStepWindow, legTwoRoute],
  );
  return legTwoRoute ? (
    <>
      {isCTS || showPrintDirectionView ? (
        <>
          <div
            className={
              showPrintDirectionView
                ? styles.ctsContainerPrint
                : styles.ctsContainer
            }
          >
            {legTwoRoute?.navSteps.map(
              (step: NavStep, index) =>
                ctsStepRange <= index &&
                index < ctsStepRange + ctsStepWindow && (
                  <LegTwoVisualStep
                    key={index}
                    step={step}
                    index={index}
                    isLastStep={legTwoRoute?.navSteps.length - 1 === index}
                    onExit={onExit}
                    isFirstStep={index === 0}
                    site={selectedSite}
                    isCtsStep={true}
                    nextStep={legTwoRoute?.navSteps[index + 1]}
                  />
                ),
            )}
          </div>
          {!showPrintDirectionView && (
            <div className={styles.ctsPageFooter}>
              <div className={styles.leftBlock}>
                <ButtonStyled
                  onClick={() => setCtsStepRange(ctsStepRange - ctsStepWindow)}
                  disabled={Boolean(ctsStepRange <= 0)}
                  kioskSmall
                  uppercase
                >
                  <Icon icon={IconType.LEFT} size={32} />
                  {t('campusNavigation.directions.previous')}
                </ButtonStyled>
              </div>
              <div className={styles.footerBlock}>
                <h2 className={styles.instruction}>
                  {`${t(
                    'campusNavigation.directions.page',
                  )} ${currentPage} / ${totalPages}`}
                </h2>
              </div>
              <div className={styles.rightBlock}>
                <ButtonStyled onClick={goToNextPage} kioskSmall uppercase>
                  {isLastPage
                    ? t('campusNavigation.directions.exit')
                    : t('campusNavigation.directions.continue')}
                  <Icon icon={IconType.RIGHT} size={32} />
                </ButtonStyled>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {legTwoRoute.navSteps.map(
            (step: NavStep, index) =>
              legTwoFocusedStep === index && (
                <LegTwoVisualStep
                  key={index}
                  step={step}
                  index={index}
                  isLastStep={legTwoRoute?.navSteps.length - 1 === index}
                  onExit={onExit}
                  isFirstStep={index === 0}
                  site={selectedSite}
                  nextStep={legTwoRoute?.navSteps[index + 1]}
                />
              ),
          )}
        </>
      )}
    </>
  ) : (
    <></>
  );
};
